import { useCallback, useEffect, useRef } from "react";
import { useGetProducts } from "../../api/get-products";
import { useGetRecommendations } from "../../api/get-recommendations";
import type { PurchaseCatalogProduct } from "../../api/vouchers-distribution.types";
import type { Recommendation } from "../../models/recommendation";
import { useCartState } from "../../store/use-cart-state";
import { useCustomerDataState } from "../../store/use-customer-data-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import type { CustomerData } from "../../types/customer-data";
import { configName } from "../config/config-name";

export const useCreateOrderFromRecommendation = () => {
  const { isCreatedFromRecommendation, addPurchaseCatalogProduct, setIsCreatedFromRecommendation } = useCartState();
  const { setCustomerData } = useCustomerDataState();
  const hasPrefilledCustomerFromRecommendationRef = useRef(false);
  const { voucherDistribution } = useVouchersDistributionState();
  const labplusRecommendationQuery = useGetRecommendations({});

  const productsQuery = useGetProducts({
    slug: voucherDistribution ? voucherDistribution.slug : "",
    configName,
  });

  const getProductsToAdd = useCallback((catalogProducts: PurchaseCatalogProduct[], recommendation: Recommendation) => {
    return catalogProducts.filter((product) => {
      if (product.productType !== "blood-test") return false;
      return recommendation.bloodTestIds.includes(product.id);
    });
  }, []);

  const createCustomerDataFromRecommendation = useCallback(
    (recommendation: Recommendation): CustomerData => ({
      firstName: recommendation.firstName,
      lastName: recommendation.lastName,
      email: recommendation.email,
      dialCode: recommendation.dialCode || "+48",
      phone: recommendation.phone,
      emailConfirmation: recommendation.email,
      acceptDataProcessing: false,
      acceptTouNPp: false,
      noPesel: false,
      pesel: "",
      dateOfBirth: undefined,
      gender: "",
      acceptNewsletter: false,
      acceptMarketingPhoneContact: false,
    }),
    [],
  );

  useEffect(() => {
    const catalogProductsRaw = productsQuery.data?.products;
    const recommendation = labplusRecommendationQuery.data;

    if (!recommendation) {
      return;
    }

    if (isCreatedFromRecommendation) {
      return;
    }

    if (!catalogProductsRaw || labplusRecommendationQuery.error) {
      return;
    }

    if (!hasPrefilledCustomerFromRecommendationRef.current) {
      setCustomerData(createCustomerDataFromRecommendation(recommendation));
      hasPrefilledCustomerFromRecommendationRef.current = true;
    }

    const productsToAdd = getProductsToAdd(catalogProductsRaw, recommendation);

    if (productsToAdd.length > 0) {
      productsToAdd.forEach(addPurchaseCatalogProduct);
    }

    setIsCreatedFromRecommendation(true);
  }, [
    addPurchaseCatalogProduct,
    setCustomerData,
    setIsCreatedFromRecommendation,
    productsQuery.data,
    labplusRecommendationQuery.data,
    labplusRecommendationQuery.error,
    isCreatedFromRecommendation,
    createCustomerDataFromRecommendation,
    getProductsToAdd,
  ]);

  return {
    isLoading: productsQuery.isLoading || labplusRecommendationQuery.isLoading,
  };
};
